import { codeMap, download, http, resource } from './http';

/**
 * 문서 · AI 추출값 — 목록 · 고치기 · 지우기는 공통 REST, 올리기 · 추출 · 검수 반영은 전용(`/api/documents/…`).
 * ⚠️ 추출값은 검수 반영(apply)을 눌러야 재무에 들어간다.
 */
export interface Doc {
  id: number; companyId: number | null; vehicleId: number | null; kind: string; fileName: string; storageKey: string;
  mimeType: string | null; sizeBytes: number | null; scope: 'shared' | 'internal';
  extractStatus: 'pending' | 'extracted' | 'checked' | 'skipped'; createdAt: string;
}
export interface DocField {
  id: number; documentId: number; label: string; value: string; confidence: number | null;
  target: string | null; isChecked: boolean; checkedAt: string | null;
}

export const DOC_KIND = codeMap({
  financials: '재무제표', biz_reg: '사업자등록증', corp_reg: '법인등기부등본', investment_contract: '투자계약서',
  fund_agreement: '조합규약', ir: 'IR', shareholders: '주주명부', other: '기타',
} as const);
export const DOC_STATUS = codeMap({ pending: '추출 대기', extracted: '추출됨', checked: '검수 완료', skipped: '안 함' } as const);
export const DOC_SCOPE = codeMap({ shared: '공유', internal: '내부 전용' } as const);
export const ACCEPT = '.pdf,.xlsx,.xls,.csv,.docx,.hwp,.hwpx,.jpg,.jpeg,.png';

const docs = resource<Doc>('documents');

/** 파일명으로 종류를 짐작한다 — 틀리면 문서함에서 고친다 */
export function guessKind(name: string): string {
  const n = name.replace(/\s/g, '');
  return /재무|손익|대차|financial/i.test(n) ? 'financials'
    : /사업자/.test(n) ? 'biz_reg'
    : /등기/.test(n) ? 'corp_reg'
    : /계약/.test(n) ? 'investment_contract'
    : /규약/.test(n) ? 'fund_agreement'
    : /주주/.test(n) ? 'shareholders'
    : /IR|소개/i.test(n) ? 'ir' : 'other';
}

export const documentApi = {
  async list(q?: { companyId?: number }): Promise<Doc[]> {
    return (await docs.list(q)).sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id - a.id);
  },
  update: (id: number, p: Partial<Doc>) => docs.update(id, p),
  remove: (id: number) => docs.remove(id),
  fields: (id: number) => http.get<DocField[]>(`/documents/${id}/fields`),
  download: (id: number, name: string) => download(`/documents/${id}/file`, name),
  upload(file: File, meta: { companyId?: number | null; vehicleId?: number | null; kind?: string; scope?: string }): Promise<Doc> {
    const fd = new FormData();
    fd.append('file', file);
    for (const [k, v] of Object.entries(meta)) if (v !== null && v !== undefined) fd.append(k, String(v));
    return http.post<Doc>('/documents/upload', fd);
  },
  extract: (id: number) => http.post<DocField[]>(`/documents/${id}/extract`),
  apply: (id: number, ids: number[], values: Record<number, string>) => http.post(`/documents/${id}/apply`, { ids, values }),
};
